import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { ApiError, apiFetchJson } from "@rook/shared/lib/apiFetch";
import type { Environment } from "@rook/shared/lib/appContext";

import type { SDKCredentials, UpdateSdkCredentialsBody } from "./types";

const sdkKeysQueryKey = (env: Environment) =>
  ["sdk-keys", "current", env] as const;

const sdkKeysPath = (env: Environment) =>
  `/api/proxy/clients/${env}/client/sdk_auth`;

/**
 * Empty-shape fallback for the 404 case — "no SDK credentials
 * registered yet for this env". Treating 404 as a success branch
 * (returning a `bundle_id: ""` payload) keeps the view as a
 * single happy path: it always renders, just in edit mode when
 * nothing is configured.
 *
 * Mirrors the legacy server action's 404 swallow in
 * `refactor-next-stable:src/modules/settings/server/sdkAuthActions.ts`.
 */
const emptyCredentials = (): SDKCredentials => ({
  client_id: "",
  client_uuid: "",
  bundle_id: "",
  package_name: "",
  created_at: "",
  updated_at: "",
});

export const useSdkKeys = (env: Environment) =>
  useQuery({
    queryKey: sdkKeysQueryKey(env),
    queryFn: async () => {
      try {
        return await apiFetchJson<SDKCredentials>(sdkKeysPath(env));
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return emptyCredentials();
        }
        throw err;
      }
    },
  });

/**
 * PUT `/client/sdk_auth`. The upstream requires the full triple
 * on every write (partial updates clobber the rest) — that's why
 * saving any one field rotates the SDK secret in the deployed apps.
 * On success, invalidate so the next read picks up the refreshed
 * `updated_at` and any persisted changes.
 */
export const useUpdateSdkKeys = (env: Environment) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateSdkCredentialsBody) =>
      apiFetchJson<{ ok?: boolean }>(sdkKeysPath(env), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sdkKeysQueryKey(env) });
    },
  });
};
